'use client';

import { useState } from 'react';
import { Download, Eye, Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { formatFileSize } from '@kit/shared/lib/file-utilities';
import { Button } from '@kit/ui/button';
import { useConfirmDialog } from '@kit/ui/confirm-dialog';
import { Trans } from '@kit/ui/trans';

import { useDocumentDownload } from '../hooks/use-document-download';
import type { Document, DocumentDisplayProps } from '../types';
import { formatDocumentDate } from '../utils/date-utils';
import { FileTypeIcon } from './file-type-icon';

interface DocumentRowProps<
  T extends Document = Document,
> extends DocumentDisplayProps<T> {
  renderBadge?: (document: T) => React.ReactNode;
  showDate?: boolean;
}

export function DocumentRow<T extends Document = Document>({
  document,
  onView,
  onDelete,
  showActions = true,
  showDate = true,
  className = '',
  renderBadge,
}: DocumentRowProps<T>) {
  const [isDeleting, setIsDeleting] = useState(false);
  const { download, isDownloading } = useDocumentDownload();
  const { confirm, ConfirmDialogComponent } = useConfirmDialog();
  const { t } = useTranslation();

  const handleDelete = async () => {
    if (!onDelete) return;

    const confirmed = await confirm({
      title: t('documents:confirmDeleteTitle'),
      description: t('documents:confirmDelete', {
        fileName: document.fileName,
      }),
      confirmText: t('common:delete'),
      variant: 'destructive',
    });

    if (confirmed) {
      setIsDeleting(true);
      try {
        await onDelete();
      } catch (error) {
        console.error('Failed to delete document:', error);
      } finally {
        setIsDeleting(false);
      }
    }
  };

  const handleDownload = async () => {
    await download(document);
  };

  return (
    <>
      <div
        className={`hover:bg-accent flex items-center gap-4 rounded-lg border p-4 transition-colors ${className}`}
      >
        <div className="flex-shrink-0">
          <FileTypeIcon mimeType={document.mimeType} />
        </div>

        <div className="min-w-0 flex-1">
          <div className="mb-1 flex items-center gap-2">
            <h4 className="truncate text-sm font-medium">
              {document.fileName}
            </h4>
            {renderBadge && renderBadge(document)}
          </div>
          <div className="text-muted-foreground flex items-center gap-2 text-xs">
            <span>{formatFileSize(document.fileSize)}</span>
            {showDate && document.uploadedAt && (
              <>
                <span>•</span>
                <span>
                  <Trans
                    i18nKey="documents:card.uploaded"
                    values={{ date: formatDocumentDate(document.uploadedAt) }}
                  />
                </span>
              </>
            )}
          </div>
          {document.description && (
            <p className="text-muted-foreground mt-1 line-clamp-1 text-xs">
              {document.description}
            </p>
          )}
        </div>

        {showActions && (
          <div className="flex items-center gap-2">
            {onView && (
              <Button variant="outline" size="sm" onClick={onView}>
                <Eye className="mr-1 h-4 w-4" />
                <Trans i18nKey="documents:card.view" />
              </Button>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={handleDownload}
              disabled={isDownloading}
            >
              <Download className="h-4 w-4" />
              <span className="sr-only">
                <Trans i18nKey="documents:card.download" />
              </span>
            </Button>
            {onDelete && (
              <Button
                variant="destructive"
                size="sm"
                onClick={handleDelete}
                disabled={isDeleting}
              >
                <Trash2 className="h-4 w-4" />
                <span className="sr-only">
                  <Trans i18nKey="documents:card.delete" />
                </span>
              </Button>
            )}
          </div>
        )}
      </div>
      <ConfirmDialogComponent />
    </>
  );
}
